import { Body2, DividerLine, H6, Tag } from '@cointracker/styleguide';
import { Grid } from '@cointracker/styleguide/src/Grid';
import { SkeletonLine } from '@cointracker/styleguide/src/Skeleton';
import ArrowsConvertIcon from '@cointracker/styleguide/src/icons/arrows-convert.svg?react';
import { useEffect } from 'react';
import { useBoundCalculatorStore } from '../../Calculator/store';
import type {
  CoinInfoType,
  CoinsMin1Length,
  CurrenciesMin1Length,
  CurrencyInfoType,
} from '../../Calculator/types';
import { CoinSelect } from './CoinSelect';
import { CurrencySelect } from './CurrencySelect';
import {
  calculator,
  container,
  convertIcon,
  divider,
  inputGroup,
  resultDetail,
  resultDetailsContainer,
  resultGroup,
  resultTitle,
  selectGroup,
  tag,
} from './TradeCalculator.css';
import { ValueInput } from './ValueInput';

export const TradeCalculator = ({
  coins: coinProps,
  currencies: currenciesProps,
}: {
  coins: CoinInfoType[];
  currencies: CurrencyInfoType[];
}) => {
  const {
    isInit,
    init,
    buyPrice,
    sellPrice,
    investment,
    investmentFee,
    exitFee,
    profitLoss,
    profitLossPercentage,
    totalExitAmount,
    totalInvestmentAmount,
    setBuyPrice,
    setSellPrice,
    setInvestment,
    setInvestmentFee,
    setExitFee,
  } = useBoundCalculatorStore((state) => ({
    isInit: state.isInit,
    init: state.init,
    buyPrice: state.buyPrice,
    sellPrice: state.sellPrice,
    investment: state.investment,
    investmentFee: state.investmentFee,
    exitFee: state.exitFee,
    profitLoss: state.profitLoss,
    profitLossPercentage: state.profitLossPercentage,
    totalExitAmount: state.totalExitAmount,
    totalInvestmentAmount: state.totalInvestmentAmount,
    setBuyPrice: state.setBuyPrice,
    setSellPrice: state.setSellPrice,
    setInvestment: state.setInvestment,
    setInvestmentFee: state.setInvestmentFee,
    setExitFee: state.setExitFee,
  }));

  useEffect(() => {
    if (coinProps?.length > 0 && currenciesProps?.length > 0) {
      init(
        coinProps as CoinsMin1Length,
        currenciesProps as CurrenciesMin1Length,
      );
    }
  }, [coinProps, currenciesProps, init, isInit]);

  if (coinProps.length === 0 || currenciesProps.length === 0 || !isInit) {
    return <TradeCalculatorLoading />;
  }

  return (
    <Grid className={container}>
      <div className={calculator}>
        <div className={selectGroup}>
          <CurrencySelect />
          <ArrowsConvertIcon className={convertIcon} width={24} height={24} />
          <CoinSelect />
        </div>

        <div className={inputGroup}>
          <ValueInput
            label="Investment"
            value={investment}
            action={setInvestment}
          />
          <ValueInput label="Buy price" value={buyPrice} action={setBuyPrice} />
          <ValueInput
            label="Sell price"
            value={sellPrice}
            action={setSellPrice}
          />
          <ValueInput
            label="Investment fee"
            value={investmentFee}
            action={setInvestmentFee}
          />
          <ValueInput label="Exit fee" value={exitFee} action={setExitFee} />
        </div>
        <DividerLine className={divider} />
        <div className={resultGroup}>
          <H6 className={resultTitle}>Investment results</H6>

          <div className={resultDetailsContainer}>
            <div className={resultDetail}>
              <Body2>Profit / Loss</Body2>
              <Tag
                className={tag}
              >{`${profitLoss} (${profitLossPercentage}%)`}</Tag>
            </div>

            <div className={resultDetail}>
              <Body2>Total investment amount</Body2>
              <Tag className={tag}>{totalInvestmentAmount}</Tag>
            </div>

            <div className={resultDetail}>
              <Body2>Total exit amount</Body2>
              <Tag className={tag}>{totalExitAmount}</Tag>
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export const TradeCalculatorLoading = () => {
  return (
    <Grid className={container}>
      <div className={calculator}>
        <div className={selectGroup}>
          <CurrencySelect loading />
          <SkeletonLine width={24} height={24} />
          <CoinSelect loading />
        </div>

        <div className={inputGroup}>
          <ValueInput loading />
          <ValueInput loading />
          <ValueInput loading />
          <ValueInput loading />
          <ValueInput loading />
        </div>
        <DividerLine className={divider} />
        <div className={resultGroup}>
          <SkeletonLine height={28} />

          <div className={resultDetailsContainer}>
            <div className={resultDetail}>
              <SkeletonLine height={20} width={102} />
              <SkeletonLine height={20} width={102} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={176} />
              <SkeletonLine height={20} width={52} />
            </div>

            <div className={resultDetail}>
              <SkeletonLine height={20} width={120} />
              <SkeletonLine height={20} width={52} />
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};
